import React from 'react';
import { Button } from '../../ui/Button';
import { CheckCircle2, Pencil, FileText } from 'lucide-react';
interface BusinessInfoData {
  businessName: string;
  tradingName: string;
  mccCode: string;
  companyType: string;
  registrationDate: string;
  website: string;
  socialMedia: string;
  registrationDocument: File | null;
  registeredAddress: string;
  registeredCity: string;
  registeredState: string;
  registeredZip: string;
  correspondenceAddress: string;
  correspondenceCity: string;
  correspondenceState: string;
  correspondenceZip: string;
  sameAsRegistered: boolean;
  annualRevenue: string;
  monthlyTransactions: string;
}
interface BusinessInfoReviewProps {
  data: BusinessInfoData;
  onConfirm: () => void;
  onBack: () => void;
  onEdit: (step: number) => void;
}
export function BusinessInfoReview({
  data,
  onConfirm,
  onBack,
  onEdit
}: BusinessInfoReviewProps) {
  const sections = [{
    title: 'Business Details',
    step: 0,
    content: <>
          <div className="font-medium" data-id="element-95">{data.businessName}</div>
          {data.tradingName && <div className="text-sm text-gray-500" data-id="element-96">
              Trading as: {data.tradingName}
            </div>}
        </>
  }, {
    title: 'Type & Industry',
    step: 1,
    content: <>
          <div data-id="element-97">Type: {data.companyType.replace('_', ' ').toUpperCase()}</div>
          <div className="text-sm text-gray-500" data-id="element-98">MCC: {data.mccCode}</div>
        </>
  }, {
    title: 'Registration',
    step: 2,
    content: `Registered on: ${data.registrationDate}`
  }, {
    title: 'Online Presence',
    step: 3,
    content: <>
          {data.website ? <div data-id="element-99">{data.website}</div> : <div className="text-gray-400 italic" data-id="element-100">No website provided</div>}
          {data.socialMedia && <div className="text-sm text-gray-500" data-id="element-101">{data.socialMedia}</div>}
        </>
  }, {
    title: 'Document',
    step: 4,
    content: data.registrationDocument ? <div className="flex items-center gap-2 text-sm text-blue-600" data-id="element-102">
          <FileText className="w-4 h-4" data-id="element-103" />
          {data.registrationDocument.name}
        </div> : <div className="text-red-500" data-id="element-104">Missing document</div>
  }, {
    title: 'Registered Address',
    step: 5,
    content: <>
          {data.registeredAddress}
          <br data-id="element-105" />
          {data.registeredCity}, {data.registeredState} {data.registeredZip}
        </>
  }, {
    title: 'Correspondence Address',
    step: 6,
    content: data.sameAsRegistered ? <span className="text-gray-500 italic" data-id="element-106">Same as registered address</span> : <>
          {data.correspondenceAddress}
          <br data-id="element-107" />
          {data.correspondenceCity}, {data.correspondenceState}{' '}
          {data.correspondenceZip}
        </>
  }, {
    title: 'Financials',
    step: 7,
    content: <>
          <div data-id="element-108">Rev: {data.annualRevenue.replace(/_/g, ' ')}</div>
          <div className="text-sm text-gray-500" data-id="element-109">
            Vol: {data.monthlyTransactions.replace(/_/g, ' ')}
          </div>
        </>
  }];
  return <div className="flex flex-col h-full max-w-md mx-auto pt-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-110">
      <div className="flex-1 overflow-y-auto pr-2" data-id="element-111">
        <div className="flex items-center gap-3 mb-2" data-id="element-112">
          <div className="p-2 bg-green-100 rounded-full" data-id="element-113">
            <CheckCircle2 className="w-6 h-6 text-green-600" data-id="element-114" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900" data-id="element-115">
            Review Business Info
          </h2>
        </div>
        <p className="text-gray-500 mb-8 ml-14" data-id="element-116">
          Please ensure all details are correct.
        </p>

        <div className="space-y-4 pb-4" data-id="element-117">
          {sections.map(section => <div key={section.title} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:border-gray-300 transition-colors" data-id="element-118">
              <div className="flex justify-between items-start mb-1" data-id="element-119">
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-120">
                  {section.title}
                </span>
                <button onClick={() => onEdit(section.step)} className="text-gray-400 hover:text-blue-600 transition-colors" aria-label={`Edit ${section.title}`} data-id="element-121">
                  <Pencil className="w-4 h-4" data-id="element-122" />
                </button>
              </div>
              <div className="text-gray-900 font-medium text-sm" data-id="element-123">
                {section.content}
              </div>
            </div>)}
        </div>
      </div>

      <div className="flex gap-4 mt-4 pb-8 pt-4 border-t border-gray-100" data-id="element-124">
        <Button variant="secondary" onClick={onBack} className="w-1/3" data-id="element-125">
          Back
        </Button>
        <Button variant="primary" onClick={onConfirm} className="w-2/3" data-id="element-126">
          Confirm & Save
        </Button>
      </div>
    </div>;
}